// The cabinets' outcome maths, mirrored from the contracts for practice mode.
//
// Every function here has a twin in casino/contracts/games/*.sol, and casino/test/practice.test.ts
// runs both against the same inputs on the real compiled bytecode. Practice mode feeds these a
// real drand randomness and a local bet id, so a practice outcome is exactly what the deployed
// cabinet would have paid for that round. Only the money and the settlement are missing.
//
// Nothing in this file talks to a wallet, a contract or the network.
import { keccak256Hex } from './keccak';

const ZERO = BigInt(0);
const ONE = BigInt(1);
const WAD = BigInt('1000000000000000000');
export const BPS = BigInt(10000);
export const FEE_BPS = BigInt(500); // House.FEE_BPS — 5%, fee-inclusive
export const RTP_BPS = BigInt(9000); // BlueScreen.RTP_BPS and Mines.RTP_BPS
export const UINT256_MAX = (ONE << BigInt(256)) - ONE;

export const TILES = 25;
export const MAX_MULT_BPS = BigInt(1000000); // Mines.MAX_MULT_BPS — 100x
export const MIN_LINE_BPS = 10100; // BlueScreen — 1.01x
export const MAX_LINE_BPS = 160000; // BlueScreen — 16.00x
export const MAX_COINS = 4; // CoinFlip
export const MAX_PACKETS = 20; // DialUp
export const DIALUP_ROWS = 8;
export const MIN_DELAY = 15; // GameBase.MIN_DELAY — seconds a real bet names its round ahead
export const ROULETTE_POCKETS = 37;
export const ROULETTE_PAY_NUMERATOR_BPS = BigInt(360000); // Roulette.PAY_NUMERATOR_BPS — 36x on one number
export const DICE_MIN_TARGET = 90; // Dice.MIN_TARGET — roll under 0.90 pays exactly 100x
export const DICE_MAX_TARGET = 8900; // Dice.MAX_TARGET — roll under 89.00 pays 1.0112x
export const DICE_ROLL_SPACE = BigInt(10000);
export const WHEEL_SEGMENTS = 50;
export const WHEEL_MAX_SPINS = 10;
export const WHEEL_MAX_MULT_BPS = BigInt(60000);
export const SLOTS_STOPS = 16;
export const SLOTS_MAX_MULT_BPS = BigInt(1000000); // three globes, 100x

const word = v => BigInt(v).toString(16).padStart(64, '0');

/** keccak256(abi.encodePacked(bytes32 randomness, uint256 id, uint256 index)) as a uint256. */
export function draw(randomness, id, index) {
  const r = String(randomness).replace(/^0x/, '').toLowerCase();
  if (r.length !== 64) throw new Error('randomness must be 32 bytes');
  return BigInt(keccak256Hex('0x' + r + word(id) + word(index)));
}

/** House.splitAllIn — the number a player types is the number they spend. */
export function splitAllIn(allIn) {
  const stake = (BigInt(allIn) * BPS) / (BPS + FEE_BPS);
  return { stake, fee: BigInt(allIn) - stake };
}

/** GameBase._open — what the house sets aside for a bet: the biggest win, floored at the stake, plus the fee. */
export function reservation(stake, fee, payoutMultBps) {
  const win = (stake * BigInt(payoutMultBps)) / BPS;
  return (win < stake ? stake : win) + fee;
}

// ── THE BLUE SCREEN ──────────────────────────────────────────────────────────

function uniform(randomness, id) {
  return draw(randomness, id, 0) % WAD;
}

/** BlueScreen.chanceWad */
export function blueScreenChanceWad(lineBps) {
  return (RTP_BPS * WAD) / BigInt(lineBps);
}

/** BlueScreen.uptimeBps — the altitude the boot reached, in bps; 0 means it never cleared POST. */
export function blueScreenUptimeBps(id, randomness) {
  const u = uniform(randomness, id);
  if (u === ZERO) return UINT256_MAX;
  const up = (RTP_BPS * BPS * WAD) / u / BPS;
  return up < BPS ? ZERO : up;
}

/** BlueScreen._payoutOf */
export function blueScreenPayout(id, stake, lineBps, randomness) {
  const line = BigInt(lineBps);
  if (uniform(randomness, id) * line <= RTP_BPS * WAD) return (BigInt(stake) * line) / BPS;
  return ZERO;
}

// ── COINFLIP.EXE ─────────────────────────────────────────────────────────────

/** CoinFlip.payoutMultBps — 2^N, exactly. */
export function coinFlipMultBps(coins) {
  return BPS << BigInt(coins);
}

/** CoinFlip.facesOf — one bit per coin, 1 = heads. */
export function coinFacesOf(id, coins, randomness) {
  return draw(randomness, id, 0) & ((ONE << BigInt(coins)) - ONE);
}

/** CoinFlip._payoutOf */
export function coinFlipPayout(id, stake, coins, side, randomness) {
  const mask = (ONE << BigInt(coins)) - ONE;
  const want = Number(side) === 1 ? mask : ZERO;
  if (coinFacesOf(id, coins, randomness) === want) return (BigInt(stake) * coinFlipMultBps(coins)) / BPS;
  return ZERO;
}

// ── DIAL-UP ──────────────────────────────────────────────────────────────────

/** DialUp.multBps */
export function dialUpMultBps(port) {
  const p = Number(port);
  if (p === 0 || p === 8) return BigInt(560000);
  if (p === 1 || p === 7) return BigInt(15000);
  if (p === 2 || p === 6) return BigInt(6000);
  if (p === 3 || p === 5) return BigInt(4000);
  return BigInt(2286);
}

/** DialUp.weightOf */
export function dialUpWeight(port) {
  const p = Number(port);
  if (p === 0 || p === 8) return 1;
  if (p === 1 || p === 7) return 8;
  if (p === 2 || p === 6) return 28;
  if (p === 3 || p === 5) return 56;
  return 70;
}

/** DialUp.portOf — eight fair bits, the port is how many went right. */
export function dialUpPortOf(id, packet, randomness) {
  const bits = draw(randomness, id, packet);
  let right = 0;
  for (let r = 0; r < DIALUP_ROWS; r += 1) right += Number((bits >> BigInt(r)) & ONE);
  return right;
}

/** DialUp._payoutOf */
export function dialUpPayout(id, stake, packets, randomness) {
  const n = Number(packets);
  const perPacket = BigInt(stake) / BigInt(n);
  let payout = ZERO;
  for (let p = 0; p < n; p += 1) payout += (perPacket * dialUpMultBps(dialUpPortOf(id, p, randomness))) / BPS;
  return payout;
}

// ── MINESWEEPER: HARD MODE ───────────────────────────────────────────────────

/** Mines.popcount */
export function popcount(x) {
  let v = BigInt(x);
  let n = 0;
  for (let i = 0; i < 32; i += 1) {
    n += Number(v & ONE);
    v >>= ONE;
  }
  return n;
}

/** Mines.payoutMultBps — the fair price of surviving k picks with m mines, times 0.90. */
export function minesPayoutMultBps(mines, k) {
  let m = RTP_BPS * WAD;
  for (let i = 0; i < Number(k); i += 1) {
    m = (m * BigInt(TILES - i)) / BigInt(TILES - Number(mines) - i);
  }
  return m / WAD;
}

/** Mines.chanceWad */
export function minesChanceWad(mines, k) {
  let p = WAD;
  for (let i = 0; i < Number(k); i += 1) {
    p = (p * BigInt(TILES - Number(mines) - i)) / BigInt(TILES - i);
  }
  return p;
}

/** Mines.mineMaskOf — a partial Fisher-Yates over the 25 tiles, one draw per mine. */
export function minesMaskOf(id, mines, randomness) {
  const slot = [];
  for (let i = 0; i < TILES; i += 1) slot.push(i);
  let mask = ZERO;
  for (let i = 0; i < Number(mines); i += 1) {
    const r = draw(randomness, id, i);
    const j = i + Number(r % BigInt(TILES - i));
    const tmp = slot[i];
    slot[i] = slot[j];
    slot[j] = tmp;
    mask |= ONE << BigInt(slot[i]);
  }
  return mask;
}

/** Mines._payoutOf */
export function minesPayout(id, stake, mines, pickMask, randomness) {
  const pick = BigInt(pickMask);
  if ((minesMaskOf(id, mines, randomness) & pick) === ZERO) {
    return (BigInt(stake) * minesPayoutMultBps(mines, popcount(pick))) / BPS;
  }
  return ZERO;
}

// ── ROULETTE.EXE ─────────────────────────────────────────────────────────────

/** Roulette.popcount over a 37-bit mask (a BigInt). */
export function roulettePopcount(mask) {
  let n = 0;
  let x = BigInt(mask);
  for (let i = 0; i < ROULETTE_POCKETS; i += 1) {
    if ((x & ONE) === ONE) n += 1;
    x >>= ONE;
  }
  return n;
}

/** Roulette.payoutMultBps — 36/n in basis points. */
export function roulettePayoutMultBps(n) {
  return ROULETTE_PAY_NUMERATOR_BPS / BigInt(n);
}

/** Roulette.chanceWad — n/37 scaled to 1e18. */
export function rouletteChanceWad(n) {
  return (BigInt(n) * WAD) / BigInt(ROULETTE_POCKETS);
}

/** Roulette.pocketOf — where the ball landed, 0..36. */
export function roulettePocketOf(id, randomness) {
  return Number(draw(randomness, id, 0) % BigInt(ROULETTE_POCKETS));
}

/** Roulette._payoutOf */
export function roulettePayout(id, stake, mask, randomness) {
  const m = BigInt(mask);
  const pocket = roulettePocketOf(id, randomness);
  if (((m >> BigInt(pocket)) & ONE) === ONE) {
    return (BigInt(stake) * roulettePayoutMultBps(roulettePopcount(m))) / BPS;
  }
  return ZERO;
}

/** The European wheel, clockwise from the zero, and the pocket colours the layout prints. */
export const ROULETTE_WHEEL = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
const ROULETTE_RED = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
export function rouletteColor(pocket) {
  if (pocket === 0) return 'green';
  return ROULETTE_RED.indexOf(pocket) >= 0 ? 'red' : 'black';
}
/** A mask from a list of pockets. */
export function rouletteMask(pockets) {
  let m = ZERO;
  for (let i = 0; i < pockets.length; i += 1) m |= ONE << BigInt(pockets[i]);
  return m;
}

// ── DICE.EXE ─────────────────────────────────────────────────────────────────

/** Dice.payoutMultBps — 0.90 / chance, in basis points. */
export function dicePayoutMultBps(target) {
  return (RTP_BPS * DICE_ROLL_SPACE) / BigInt(target);
}

/** Dice.chanceWad — target/10000 scaled to 1e18. */
export function diceChanceWad(target) {
  return (BigInt(target) * WAD) / DICE_ROLL_SPACE;
}

/** Dice.rollOf — what the dice rolled, in hundredths 0..9999. */
export function diceRollOf(id, randomness) {
  return Number(draw(randomness, id, 0) % DICE_ROLL_SPACE);
}

/** Dice._payoutOf */
export function dicePayout(id, stake, target, randomness) {
  if (diceRollOf(id, randomness) < Number(target)) {
    return (BigInt(stake) * dicePayoutMultBps(target)) / BPS;
  }
  return ZERO;
}

// ── THE WHEEL ────────────────────────────────────────────────────────────────

/** Wheel.LAYOUT — codes 0 = 0x, 1 = 1.5x, 2 = 2x, 3 = 3x, 4 = 6x, clockwise from the top. */
export const WHEEL_LAYOUT = [0, 1, 0, 2, 0, 1, 0, 3, 0, 0, 1, 0, 2, 0, 1, 0, 4, 0, 0, 1, 0, 2, 0, 1, 0, 3, 0, 0, 1, 0, 2, 0, 1, 0, 1, 0, 0, 2, 0, 1, 0, 3, 0, 1, 0, 0, 2, 0, 1, 0];
const WHEEL_MULTS = [0, 15000, 20000, 30000, 60000];

/** Wheel.multBps */
export function wheelMultBps(segment) {
  return BigInt(WHEEL_MULTS[WHEEL_LAYOUT[segment]]);
}

/** Wheel.segmentOf */
export function wheelSegmentOf(id, spinIndex, randomness) {
  return Number(draw(randomness, id, spinIndex) % BigInt(WHEEL_SEGMENTS));
}

/** Wheel._payoutOf — the stake split evenly across the spins. */
export function wheelPayout(id, stake, spins, randomness) {
  const n = Number(spins);
  const perSpin = BigInt(stake) / BigInt(n);
  let total = ZERO;
  for (let s = 0; s < n; s += 1) {
    total += (perSpin * wheelMultBps(wheelSegmentOf(id, s, randomness))) / BPS;
  }
  return total;
}

// ── JACKPOT.EXE ──────────────────────────────────────────────────────────────

export const SLOTS_SYMBOLS = ['mine', 'bin', 'folder', 'disk', 'clippy', 'globe'];
/** Slots.STRIP — the strip every reel carries, stop 0..15. */
export const SLOTS_STRIP = [0, 1, 2, 0, 3, 1, 0, 4, 2, 0, 5, 1, 0, 3, 2, 4];

/** Slots.symbolAt */
export function slotsSymbolAt(stop) {
  return SLOTS_STRIP[stop];
}

/** Slots.stopOf */
export function slotsStopOf(id, reel, randomness) {
  return Number(draw(randomness, id, reel) % BigInt(SLOTS_STOPS));
}

function countOf(a, b, c, s) {
  return (a === s ? 1 : 0) + (b === s ? 1 : 0) + (c === s ? 1 : 0);
}

/** Slots.payBps — highest matching line, in basis points of the stake. */
export function slotsPayBps(a, b, c) {
  if (a === b && b === c) {
    if (a === 5) return BigInt(1000000);
    if (a === 4) return BigInt(500000);
    if (a === 3) return BigInt(320000);
    if (a === 2) return BigInt(150000);
    if (a === 1) return BigInt(127000);
    return ZERO;
  }
  const globes = countOf(a, b, c, 5);
  if (globes === 2) return BigInt(100000);
  if (countOf(a, b, c, 4) === 2) return BigInt(25000);
  if (countOf(a, b, c, 3) === 2) return BigInt(20000);
  if (globes === 1) return BigInt(15000);
  return ZERO;
}

/** The three symbols a settled round showed. */
export function slotsSymbolsOf(id, randomness) {
  return [0, 1, 2].map(reel => slotsSymbolAt(slotsStopOf(id, reel, randomness)));
}

/** Slots._payoutOf */
export function slotsPayout(id, stake, randomness) {
  const s = slotsSymbolsOf(id, randomness);
  return (BigInt(stake) * slotsPayBps(s[0], s[1], s[2])) / BPS;
}

// ── tickets ──────────────────────────────────────────────────────────────────
// The same refusals the cabinets make at the door, with the contract's error names so the
// front-end's humanError() prints the same sentence for a practice refusal as for a real one.

export function ticketFor(cabinetKey, args) {
  if (cabinetKey === 'blueScreen') {
    const line = Number(args[0]);
    if (!(line >= MIN_LINE_BPS && line <= MAX_LINE_BPS)) throw new Error('BadLine()');
    return { payoutMultBps: BigInt(line) };
  }
  if (cabinetKey === 'coinFlip') {
    const coins = Number(args[0]);
    const side = Number(args[1]);
    if (!(coins >= 1 && coins <= MAX_COINS)) throw new Error('BadCoins()');
    if (side !== 0 && side !== 1) throw new Error('BadSide()');
    return { payoutMultBps: coinFlipMultBps(coins) };
  }
  if (cabinetKey === 'dialUp') {
    const packets = Number(args[0]);
    if (!(packets >= 1 && packets <= MAX_PACKETS)) throw new Error('BadPackets()');
    return { payoutMultBps: BigInt(560000) };
  }
  if (cabinetKey === 'mines') {
    const mines = Number(args[0]);
    const pick = BigInt(args[1]);
    if (!(mines >= 1 && mines < TILES)) throw new Error('BadMines()');
    if (pick >> BigInt(TILES) !== ZERO) throw new Error('BadPicks()');
    const k = popcount(pick);
    if (k === 0 || k > TILES - mines) throw new Error('BadPicks()');
    const mult = minesPayoutMultBps(mines, k);
    if (mult > MAX_MULT_BPS) throw new Error('TooRich()');
    if (mult <= BPS) throw new Error('TooCheap()');
    return { payoutMultBps: mult };
  }
  if (cabinetKey === 'roulette') {
    const mask = BigInt(args[0]);
    const all = (ONE << BigInt(ROULETTE_POCKETS)) - ONE;
    if (mask === ZERO || (mask & ~all) !== ZERO) throw new Error('BadMask()');
    const n = roulettePopcount(mask);
    if (n >= 36) throw new Error('TooCheap()');
    return { payoutMultBps: roulettePayoutMultBps(n) };
  }
  if (cabinetKey === 'dice') {
    const target = Number(args[0]);
    if (!(target >= DICE_MIN_TARGET && target <= DICE_MAX_TARGET)) throw new Error('BadTarget()');
    return { payoutMultBps: dicePayoutMultBps(target) };
  }
  if (cabinetKey === 'wheel') {
    const spins = Number(args[0]);
    if (!(spins >= 1 && spins <= WHEEL_MAX_SPINS)) throw new Error('BadSpins()');
    return { payoutMultBps: WHEEL_MAX_MULT_BPS };
  }
  if (cabinetKey === 'slots') {
    return { payoutMultBps: SLOTS_MAX_MULT_BPS };
  }
  throw new Error('unknown cabinet');
}

/** The cabinet's _payoutOf, dispatched by key. */
export function payoutFor(cabinetKey, id, stake, args, randomness) {
  if (cabinetKey === 'blueScreen') return blueScreenPayout(id, stake, args[0], randomness);
  if (cabinetKey === 'coinFlip') return coinFlipPayout(id, stake, args[0], args[1], randomness);
  if (cabinetKey === 'dialUp') return dialUpPayout(id, stake, args[0], randomness);
  if (cabinetKey === 'mines') return minesPayout(id, stake, args[0], args[1], randomness);
  if (cabinetKey === 'roulette') return roulettePayout(id, stake, args[0], randomness);
  if (cabinetKey === 'dice') return dicePayout(id, stake, args[0], randomness);
  if (cabinetKey === 'wheel') return wheelPayout(id, stake, args[0], randomness);
  if (cabinetKey === 'slots') return slotsPayout(id, stake, randomness);
  throw new Error('unknown cabinet');
}
